/**
 * Asset fetching: manifest entries -> bytes / parsed objects, with a byte-budgeted cache for
 * volumes (LRU, like the desktop ProjectManager cache) and glTF loading through meshopt.
 * Browsers decode `Content-Encoding: br` inside fetch, so volumes arrive as raw bytes.
 */
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import type { AssetEntry, Manifest } from '@/api/client';

export interface VolumeData {
  asset: AssetEntry;
  dims: [number, number, number];
  voxelSize: [number, number, number];
  bytes: Uint8Array;
  channels: 1 | 4;
}

export interface PolylineData {
  format: 'polyline';
  units: 'mm';
  vertices: [number, number, number][];
  lines: number[][];
}

export class ByteBudgetCache<T extends { byteLength: number }> {
  private map = new Map<string, T>();
  private bytes = 0;
  constructor(public budgetBytes: number) {}

  get(key: string): T | undefined {
    const v = this.map.get(key);
    if (v) {
      // refresh LRU order
      this.map.delete(key);
      this.map.set(key, v);
    }
    return v;
  }

  set(key: string, value: T, pinned: Set<string> = new Set()): void {
    if (this.map.has(key)) this.delete(key);
    this.map.set(key, value);
    this.bytes += value.byteLength;
    for (const [k, v] of this.map) {
      if (this.bytes <= this.budgetBytes) break;
      if (k === key || pinned.has(k)) continue;
      this.map.delete(k);
      this.bytes -= v.byteLength;
    }
  }

  delete(key: string): void {
    const v = this.map.get(key);
    if (v) {
      this.map.delete(key);
      this.bytes -= v.byteLength;
    }
  }

  get usedBytes(): number {
    return this.bytes;
  }
}

export async function fetchBytes(url: string, signal?: AbortSignal): Promise<Uint8Array> {
  const res = await fetch(url, { signal, mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`fetch ${res.status} for ${url.split('?')[0]}`);
  return new Uint8Array(await res.arrayBuffer());
}

export async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const res = await fetch(url, { signal, mode: 'cors', credentials: 'omit' });
  if (!res.ok) throw new Error(`fetch ${res.status} for ${url.split('?')[0]}`);
  return (await res.json()) as T;
}

export async function loadVolume(asset: AssetEntry, signal?: AbortSignal): Promise<VolumeData> {
  if (!asset.dims) throw new Error(`volume ${asset.key} has no dims`);
  const bytes = await fetchBytes(asset.url, signal);
  const voxels = asset.dims[0] * asset.dims[1] * asset.dims[2];
  const channels: 1 | 4 = asset.format === 'rgba8' ? 4 : 1;
  if (bytes.length !== voxels * channels) {
    throw new Error(`volume ${asset.key}: ${bytes.length} bytes, expected ${voxels * channels}`);
  }
  return { asset, dims: asset.dims, voxelSize: asset.voxel_size ?? [1, 1, 1], bytes, channels };
}

export function makeData3DTexture(vol: VolumeData, filter: THREE.MagnificationTextureFilter = THREE.LinearFilter): THREE.Data3DTexture {
  const tex = new THREE.Data3DTexture(vol.bytes as Uint8Array<ArrayBuffer>, vol.dims[0], vol.dims[1], vol.dims[2]);
  tex.format = vol.channels === 4 ? THREE.RGBAFormat : THREE.RedFormat;
  tex.type = THREE.UnsignedByteType;
  tex.minFilter = filter;
  tex.magFilter = filter;
  tex.wrapS = tex.wrapT = tex.wrapR = THREE.ClampToEdgeWrapping;
  tex.unpackAlignment = 1;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

let gltfLoader: GLTFLoader | null = null;
function getGltfLoader(): GLTFLoader {
  if (!gltfLoader) {
    gltfLoader = new GLTFLoader();
    gltfLoader.setMeshoptDecoder(MeshoptDecoder);
  }
  return gltfLoader;
}

/** Loads a glb and returns its geometries (merged scene meshes), in file mm. */
export async function loadSurfaceGeometries(asset: AssetEntry): Promise<THREE.BufferGeometry[]> {
  const bytes = await fetchBytes(asset.url);
  const gltf = await getGltfLoader().parseAsync(bytes.buffer as ArrayBuffer, '');
  const geoms: THREE.BufferGeometry[] = [];
  gltf.scene.updateMatrixWorld(true);
  gltf.scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && mesh.geometry) {
      const g = mesh.geometry.clone();
      g.applyMatrix4(mesh.matrixWorld);
      if (!g.getAttribute('normal')) g.computeVertexNormals();
      geoms.push(g);
    }
  });
  return geoms;
}

export async function loadPolyline(asset: AssetEntry): Promise<PolylineData> {
  return fetchJson<PolylineData>(asset.url);
}

export function assetsOfKind(manifest: Manifest, kind: AssetEntry['kind']): AssetEntry[] {
  return manifest.assets.filter((a) => a.kind === kind);
}

export function primaryCt(manifest: Manifest, frame?: number): AssetEntry | undefined {
  const cts = assetsOfKind(manifest, 'ct');
  if (frame !== undefined) return cts.find((a) => (a.frame_index ?? 0) === frame);
  return cts.find((a) => a.is_primary) ?? cts[0];
}
